<template>
  <div class="login-progress">
    <div class="content">
      <Spinner />
      <h2 class="title">{{ title }}</h2>
      <p class="message">{{ message }}</p>
    </div>
    <Notification v-if="notification" :notification="notification" />
  </div>
</template>

<script lang="ts">
import { match } from "ts-pattern";
import { computed, defineComponent, PropType } from "vue";

import type {
  LoginProgressStep,
  LoginStatusDetail,
  Notification as NotificationSchema,
} from "@ctrl/controller-rpc";

import Notification from "@/components/notification/Notification.vue";

/**
 * The non-terminal login statuses this screen displays, narrowed to the step
 * the controller always reports alongside `in_progress`.
 */
export type LoginProgressState =
  | (Extract<LoginStatusDetail, { status: "in_progress" }> & {
      step: LoginProgressStep;
    })
  | Extract<LoginStatusDetail, { status: "offline" }>;

export default defineComponent({
  name: "LoginProgress",
  components: {
    Notification,
  },
  props: {
    notification: {
      type: Object as PropType<NotificationSchema | null>,
      default: null,
    },
    state: {
      type: Object as PropType<LoginProgressState>,
      required: true,
    },
  },
  setup(props) {
    const title = computed(() =>
      match(props.state)
        .with({ status: "offline" }, () => "You seem to be offline")
        .with({ step: "from_scratch" }, () => "Loading your data")
        .with(
          { step: "migrating_account" },
          () => "Your account is being migrated"
        )
        .exhaustive()
    );
    const message = computed(() =>
      match(props.state)
        .with(
          { status: "offline" },
          () => "Login will resume as soon as you are back online."
        )
        .with(
          { step: "from_scratch" },
          () => "We need to resync this device which may take a few minutes."
        )
        .with(
          { step: "migrating_account" },
          () => "This could take a few minutes, do not close Routine."
        )
        .exhaustive()
    );
    return { title, message };
  },
});
</script>

<style scoped lang="scss">
.login-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    :deep(.container) {
      height: 24px;
      width: 24px;
      .spinner {
        height: 24px;
        width: 24px;
        margin-bottom: 16px;
      }
    }
  }

  .title {
    margin: 24px 0 12px 0;
    @extend %font-h3;
  }

  .message {
    margin: 0;
    max-width: 400px;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
